import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postDataWithAuthToken } from '../../utils/axiosRequest';
import { alertMessage } from '../../utils/alertMessage';
import TagInput from '../common/TagInput';
import TextEditor from '../textEditor/TextEditor';
import { ExclamationCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import { SendMailConfigData } from '../../types';

type SendEmailModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  emailConfig?: SendMailConfigData;
  emailHTML?: HTMLElement;
  // 发送成功后，在这些订单的 OrderAction 中记录发送邮件日志（invoice 模式）
  orderIds?: number[];
};

const SendEmailModal = ({
  visible,
  setVisible,
  emailConfig,
  emailHTML,
  orderIds,
}: SendEmailModalProps) => {
  //General Components
  const { t } = useTranslation();
  const isSubscribed = useRef(true);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  // PDF file
  const [pdfFile, setPdfFile] = useState<Blob>();

  const [isAttachPdf, setIsAttachPdf] = useState(true);
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  //Text Editor Components
  const [clear, setClear] = useState(false);
  const [MailHtmlBody, setMailHtmlBody] = useState<string>();
  const [fullscreenMode, setFullScreenMode] = useState(false);

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setPdfFile(undefined);
    setIsAttachPdf(true);
    setMailHtmlBody(undefined);
    setCc([]);
    setTo([]);
    setVisible(false);
  };

  useEffect(() => {
    if (clear) {
      form.resetFields(['htmlBody']);
      setClear(false);
    }
  }, [clear, form]);

  const previewPDF = useCallback(() => {
    // Upload htmlCode to server and get PDF file
    if (emailHTML) {
      setIsLoading(true); // Start loading

      // const spans = emailHTML.querySelectorAll('span');
      // spans.forEach((span) => {
      //   span.style.fontFamily = 'Noto Sans SC'; // Apply the custom font to every <span>
      // });

      postDataWithAuthToken(
        'send/mail/preview',
        {
          htmlAttachment: emailHTML.outerHTML,
          fileName: 'documents',
        },
        {
          responseType: 'blob',
        }
      )
        .then((response) => {
          setIsLoading(false);
          if (response) {
            const blob = new Blob([response], { type: 'application/pdf' });
            setPdfFile(blob); // Set the PDF file
          }
        })
        .catch((error) => {
          alertMessage('error',  `${t('sendEmail.alerts.generatePdfFail')}: ${error.message}`);
          setIsLoading(false); // Stop loading
          console.error('Error generating PDF:', error);
        });
    }
  }, [emailHTML, t]);

  // Things to do on first render
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setMailHtmlBody(undefined);

      if (emailConfig) {
        if (emailConfig.mailContent) setMailHtmlBody(emailConfig.mailContent);
        if (emailConfig.to) setTo(emailConfig.to);
        if (emailConfig.cc) setCc(emailConfig.cc);
        form.setFieldsValue({
          replyTo: emailConfig.replyTo,
          subject: emailConfig.mailSubject,
        });
      }
    } else {
      setMailHtmlBody(undefined); // Reset HTMLText when modal is closed
    }
  }, [visible, emailConfig, form]);

  // preview html pdf
  useEffect(() => {
    if (visible && emailHTML) {
      previewPDF();
    }
  }, [visible, emailHTML, previewPDF]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const formData = new FormData();
        formData.append('replyTo', values.replyTo);

        to.forEach((item) => {
          formData.append('to[]', item);
        });
        cc.forEach((item) => {
          formData.append('cc[]', item);
        });
        formData.append('subject', values.subject);
        formData.append('htmlBody', MailHtmlBody ? MailHtmlBody : '');
        formData.append(
          'htmlAttachments',
          emailHTML ? emailHTML.outerHTML : ''
        );

        if (isAttachPdf && pdfFile) {
          formData.append('attachments', pdfFile, 'attachments.pdf');
        }
        orderIds?.forEach((id) => {
          formData.append('orderIds[]', id.toString());
        });
        handlePostRequest(formData);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handlePostRequest = (formData: any) => {
    setIsLoading(true);
    postDataWithAuthToken('send/mail', formData)
      .then((response) => {
        setIsLoading(false); // Stop loading
        if (response && response.goodStatus) {
          alertMessage('success', t('sendEmail.alerts.sendSuccess'));

          // close after success
          onCancel();
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((err: any) => {
        setIsLoading(false); // Stop loading
        alertMessage('error', t('sendEmail.alerts.sendFail', { err: err }));
        console.error('Error sending email:', err);
      });
  };

  return (
    <Modal
      title={t('sendEmail.title')}
      visible={visible}
      width={fullscreenMode ? '100%' : 860}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={t('sendEmail.actionsColumn.send')}
      cancelText={t('general.cancel')}
      maskClosable={false}
      confirmLoading={isLoading}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Spin spinning={isLoading}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="replyTo"
            label={t('sendEmail.from')}
            rules={[
              {
                type: 'email',
                message: t('general.inputError.pleaseInputValidEmail'),
              },
              {
                required: true,
                message: t('general.inputError.empty'),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="to"
            label={t('sendEmail.sendTo')}
            required
            rules={[
              {
                required: to.length === 0,
                message: t('general.inputError.empty'),
              },
            ]}
          >
            <TagInput
              tags={to}
              setTags={setTo}
              inputRegex={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
              errorMessage={t('general.inputError.pleaseInputValidEmail')}
            />
          </Form.Item>
          <Form.Item name="cc" label={t('sendEmail.cc')}>
            <TagInput
              tags={cc}
              setTags={setCc}
              inputRegex={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
              errorMessage={t('general.inputError.pleaseInputValidEmail')}
            />
          </Form.Item>

          <Form.Item
            name="subject"
            label={t('sendEmail.subject')}
            rules={[
              {
                required: true,
                message: t('general.inputError.empty'),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="htmlBody">
            <TextEditor
              fullscreenMode={fullscreenMode}
              setFullscreenMode={setFullScreenMode}
              setClear={setClear}
              data={MailHtmlBody}
              setData={setMailHtmlBody}
            />
          </Form.Item>

          {emailHTML &&
            (pdfFile ? (
              /** 预览PDF文件 */
              <Space style={{ paddingTop: 10 }}>
                <Checkbox
                  checked={isAttachPdf}
                  onChange={(e) => {
                    setIsAttachPdf(e.target.checked);
                  }}
                >
                  {t('sendEmail.attachFile')}
                </Checkbox>
                <Button
                  type="link"
                  icon={<FilePdfOutlined />}
                  onClick={() => {
                    const fileURL = URL.createObjectURL(pdfFile);
                    window.open(fileURL, '_blank');
                    // Optionally revoke the URL after opening to free memory
                    setTimeout(() => URL.revokeObjectURL(fileURL), 100);
                  }}
                >
                  {t('sendEmail.file')}
                </Button>
              </Space>
            ) : (
              /*** 有HTML文件，但是获取PDF文件失败！ */
              <Space align="start" style={{ color: 'red' }}>
                <ExclamationCircleOutlined />
                <Typography.Paragraph>
                  {t('sendEmail.alerts.attachFileFail')}
                </Typography.Paragraph>
              </Space>
            ))}
        </Form>
      </Spin>
    </Modal>
  );
};

export default SendEmailModal;
